import { useEffect, useRef, useState } from "react"

import Check from "@untitled-ui/icons-react/build/esm/Check"
import SearchLg from "@untitled-ui/icons-react/build/esm/SearchLg"
import XClose from "@untitled-ui/icons-react/build/esm/XClose"

import { DropdownOption } from "../../models/DropdownOption"

import { classNames } from "../../utils/classNames"
import InputWrapper from "./wrapper/InputWrapper"
import Plus from "@untitled-ui/icons-react/build/esm/Plus"

const ValueTag = ({ value, onClose, disabled }: { value: string; onClose: () => void; disabled?: boolean }) => {
    return (
        <div className="flex gap-2 items-center py-0.5 px-1 rounded-sm border border-border_primary shadow-sm">
            <p className="font-medium text-sm text-text_tertiary_color">{value}</p>
            {!disabled && (
                <button
                    type="button"
                    title="close"
                    onClick={(e) => {
                        e.stopPropagation()
                        onClose()
                    }}
                >
                    <XClose className="w-3 h-3 text-fg_quarterary" />
                </button>
            )}
        </div>
    )
}

interface MultiSelectDropdownProps {
    label?: string
    handleChange: (e: any) => void
    error?: string
    options?: DropdownOption[]
    values?: DropdownOption[]
    displayValueTagsLimit?: number
    placeholder?: string
    subtitle?: string
    required?: boolean
    customRenderOption?: (object: DropdownOption) => JSX.Element
    enableCreateNew?: boolean
    onCreateNew?: (searchText?: string) => void
}

const MultiSelectDropdown = ({
    label,
    handleChange,
    error,
    values,
    placeholder,
    options,
    subtitle,
    displayValueTagsLimit,
    required,
    customRenderOption,
    enableCreateNew,
    onCreateNew
}: MultiSelectDropdownProps) => {
    const [openDropdown, setOpenDropdown] = useState(false)
    const [searchValue, setSearchValue] = useState("")
    const dropdownRef = useRef<HTMLDivElement>(null)
    const [selectedValues, setSelectedValues] = useState<DropdownOption[]>(values || [])
    const [updateProps, setUpdateProps] = useState(false)

    const inputRef = useRef<HTMLInputElement | null>(null)

    useEffect(() => {
        if (values) setSelectedValues(values)
    }, [values])

    const valueExists = (option: DropdownOption) => {
        return selectedValues?.map((value) => value.value).includes(option.value)
    }

    const handleSelectAll = () => {
        let allOptions: DropdownOption[] = []

        const disabledSelected = selectedValues.filter((val) => val.disabled)

        filteredValues(searchValue)?.map((option) => {
            if (!valueExists(option)) {
                allOptions.push(option)
            }
        })
        setSelectedValues([...disabledSelected, ...allOptions])
        setUpdateProps(true)
    }

    const handleUnSelectAll = () => {
        // let allOptions: DropdownOption[] = []
        const disabledSelected = selectedValues.filter((val) => val.disabled)
        setSelectedValues(disabledSelected)
        setUpdateProps(true)
    }

    const handleOnChange = (option: DropdownOption) => {
        if (option.disabled && valueExists(option)) {
            return
        }

        if (valueExists(option)) {
            setSelectedValues(selectedValues.filter((value) => value.value !== option.value))
        } else {
            setSelectedValues([...selectedValues, option])
        }
        setUpdateProps(true)
    }

    useEffect(() => {
        if (updateProps) {
            setUpdateProps(false)
            handleChange(selectedValues)
        }
    }, [selectedValues, updateProps])

    const handleRemoveItem = (option: DropdownOption) => {
        if (option.disabled) {
            return
        }

        setSelectedValues(selectedValues.filter((value) => value.value !== option.value))
        setUpdateProps(true)
    }

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus()
        }
        const handleClickOutside = (event: any) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpenDropdown(false)
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [openDropdown])
    const filteredValues = (inputValue: string) => {
        return options?.filter((option) => {
            return option.label.toLowerCase().includes(inputValue.toLowerCase())
        })
    }

    const handleKeyDown: React.KeyboardEventHandler = (e) => {
        if (e.key === "Enter") {
            setOpenDropdown(!openDropdown)
        }
        if (e.key === "Tab") {
            setOpenDropdown(false)
        }
    }

    const getAreMoreValuesContent = () => {
        if (!displayValueTagsLimit) return

        if (selectedValues.length > displayValueTagsLimit) {
            const remaningValueCount = selectedValues.slice(displayValueTagsLimit, -1).length

            return `& ${remaningValueCount + 1} more`
        }
    }

    return (
        <InputWrapper label={label} subtitle={subtitle} error={error} ref={dropdownRef}>
            <div
                tabIndex={0}
                className={classNames(
                    error
                        ? "border-primary_error focus:border-primary_error focus:outline-primary_error"
                        : "border-border_primary focus:border-primary_color",
                    "border p-2 w-full flex flex-wrap gap-2 items-center focus-visible:outline-none min-h-[42px] rounded-lg"
                )}
                onClick={() => {
                    setOpenDropdown((prev) => !prev)
                }}
                onKeyDown={handleKeyDown}
            >
                <SearchLg className="w-5 h-5 text-fg_quarterary" />
                {placeholder && !selectedValues.length ? (
                    <p className=" text-foreground_primary">{placeholder}</p>
                ) : null}
                {selectedValues.slice(0, displayValueTagsLimit)?.map((value, index) => (
                    <ValueTag
                        key={index}
                        value={value.label}
                        onClose={() => {
                            handleRemoveItem(value)
                        }}
                        disabled={value.disabled}
                    />
                ))}
                {getAreMoreValuesContent()}
            </div>
            {openDropdown && (
                <div className="absolute top-[100%] left-0 border border-border_secondary bg-white z-50 w-full shadow-md">
                    <div className="flex items-center gap-4 border-b border-border_secondary p-2">
                        <SearchLg className="w-5 h-5 text-fg_quarterary" />
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder={"Search"}
                            className="w-full outline-none border-none focus:outline-none focus:border-none"
                            value={searchValue}
                            onChange={(e) => {
                                setSearchValue(e.target.value)
                            }}
                        />
                    </div>
                    <div className="max-h-56 overflow-auto">
                        <div
                            key={"select_All"}
                            className={classNames(
                                "bg-white",
                                "flex items-center justify-start gap-4 py-2  cursor-pointer m-1"
                            )}
                        >
                            <p
                                className="font-medium text-sm p-2 text-secondary_color hover:bg-background_primary"
                                onClick={handleSelectAll}
                            >
                                SELECT ALL
                            </p>
                            <p
                                onClick={handleUnSelectAll}
                                className="font-medium text-sm p-2 text-secondary_color hover:bg-background_primary"
                            >
                                UNSELECT ALL
                            </p>
                        </div>
                        {filteredValues(searchValue)?.map((option) => (
                            <div
                                key={option.value}
                                onClick={() => {
                                    handleOnChange(option)
                                }}
                                className={classNames(
                                    valueExists(option) ? "bg-background_primary" : "bg-white",
                                    option.disabled && valueExists(option)
                                        ? "!opacity-70 !cursor-not-allowed"
                                        : "cursor-pointer",
                                    "flex items-center justify-between p-2 hover:bg-background_primary cursor-pointer m-1"
                                )}
                            >
                                {customRenderOption ? (
                                    customRenderOption(option)
                                ) : (
                                    <p className="font-medium text-sm text-text_primary_color">{option.label}</p>
                                )}
                                {valueExists(option) && <Check className="w-4 h-4 text-secondary_color" />}
                            </div>
                        ))}
                        {filteredValues(searchValue)?.length === 0 && enableCreateNew && (
                            <div
                                className="py-2 px-2.5 hover:bg-background_primary cursor-pointer flex items-center"
                                onClick={() => {
                                    setOpenDropdown(false)
                                    setSearchValue("")
                                    if (onCreateNew) onCreateNew(searchValue)
                                }}
                            >
                                <Plus className="w-5 h-5 text-secondary_color" />
                                <p className="font-medium text-md p-2 text-secondary_color">Create New</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </InputWrapper>
    )
}

export default MultiSelectDropdown
